import React, { useState } from "react";
import {
  Alert,
  Autosuggest,
  Box,
  Button,
  FormField,
  Modal,
  SpaceBetween,
  TextContent,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { useInvestigationAssignees } from "../../hooks/useInvestigations";

interface AssignOwnerModalProps {
  stage: Stage;
  visible: boolean;
  /** Investigation title the modal is about; shown so the picker has context. */
  title: string;
  /** Primary button label, e.g. "Create and assign". */
  confirmLabel: string;
  loading?: boolean;
  /** Error from the create call, shown inside the modal so it is not hidden behind it. */
  errorText?: string | null;
  onDismiss: () => void;
  /** Called with the normalised (trimmed, lower-case) alias. */
  onConfirm: (alias: string) => void;
}

/**
 * Pick a Lusca Search user to own an investigation that is about to be created. Mirrors the
 * reassign picker: only aliases from GET investigations/assignees (the Bindle lock members) are
 * accepted; while that directory is unavailable the backend has the final say.
 */
export const AssignOwnerModal: React.FC<AssignOwnerModalProps> = ({
  stage,
  visible,
  title,
  confirmLabel,
  loading,
  errorText,
  onDismiss,
  onConfirm,
}) => {
  const [owner, setOwner] = useState("");
  const assignees = useInvestigationAssignees(stage, visible);
  const trimmed = owner.trim().toLowerCase();
  const knownAliases = assignees.data?.aliases ?? [];
  const directoryUnavailable = !!assignees.error;
  const isKnownAlias = directoryUnavailable || knownAliases.includes(trimmed);
  const confirmDisabled = !trimmed || !isKnownAlias || assignees.isLoading;

  const enteredTextLabel = (text: string) => {
    const typed = text.trim().toLowerCase();
    if (assignees.isLoading) return `Checking "${typed}"…`;
    return directoryUnavailable || knownAliases.includes(typed)
      ? `Use alias "${typed}"`
      : `No Lusca Search user named "${typed}"`;
  };
  const aliasError =
    trimmed && !assignees.isLoading && !isKnownAlias
      ? `"${trimmed}" is not a Lusca Search user. Only people with access to this tool can own an investigation.`
      : undefined;

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header="Create and assign ownership"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => onConfirm(trimmed)}
              disabled={confirmDisabled}
              loading={loading}
            >
              {confirmLabel}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s">
        <TextContent>
          <p style={{ margin: 0 }}>
            Creates “{title}” with the person you pick as owner. They are
            notified and the audit trail records that you assigned them.
          </p>
        </TextContent>
        {directoryUnavailable && (
          <Alert type="warning">
            User list could not be loaded; the alias is checked on submit.
          </Alert>
        )}
        <FormField
          label="Owner alias"
          description="Only people with access to Lusca Search can own an investigation."
          errorText={aliasError}
        >
          <Autosuggest
            ariaLabel="Owner alias"
            placeholder="Start typing an alias"
            value={owner}
            options={knownAliases.map((alias) => ({
              value: alias,
              label: alias,
            }))}
            statusType={assignees.isLoading ? "loading" : "finished"}
            loadingText="Loading Lusca Search users"
            filteringType="auto"
            onChange={({ detail }) => setOwner(detail.value)}
            onSelect={({ detail }) =>
              setOwner(detail.selectedOption?.value ?? detail.value)
            }
            enteredTextLabel={enteredTextLabel}
            empty="No Lusca Search users found"
          />
        </FormField>
        {errorText && <Alert type="error">{errorText}</Alert>}
      </SpaceBetween>
    </Modal>
  );
};

export default AssignOwnerModal;
